
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Calendar, MapPin, Trophy, Check, X, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PartnerInvitationData {
  partnerName: string;
  eventId: string;
  eventTitle: string;
  partnerAvatar?: string;
  event?: any;
  invitation?: any;
  inviterRegistration?: {
    category?: string;
    subcategory?: string;
    modality?: string;
  };
}

interface PartnerInvitationConfirmationProps {
  invitationData: PartnerInvitationData;
  onReject: () => void;
  onBack: () => void;
  invitationId: string;
}

const PartnerInvitationConfirmation = ({ 
  invitationData, 
  onReject, 
  onBack,
  invitationId
}: PartnerInvitationConfirmationProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Use real event data if available, otherwise use defaults
  const eventDetails = invitationData.event ? {
    id: invitationData.event.id,
    title: invitationData.event.title,
    sport: invitationData.event.sport || "Pádel",
    date: invitationData.event.start_date ? new Date(invitationData.event.start_date).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES'),
    time: invitationData.event.start_time || "",
    location: invitationData.event.location || "",
    category: invitationData.event.category || "",
    subcategory: invitationData.event.subcategory || "",
    registrationFee: invitationData.event.entry_fee ? invitationData.event.entry_fee / 2 : 0, // Individual price
    description: invitationData.event.description || "Evento deportivo"
  } : {
    id: invitationData.eventId,
    title: invitationData.eventTitle,
    sport: "Pádel",
    date: new Date().toLocaleDateString('es-ES'),
    time: "",
    location: "",
    category: "",
    subcategory: "",
    registrationFee: 0,
    description: "Evento deportivo"
  };

  const handleAccept = async () => {
    setIsProcessing(true);
    
    try {
      console.log("Accepting invitation...");
      
      // Simply accept the invitation - no payment needed since the inviter already paid
      const { error: updateError } = await supabase
        .from('partner_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      toast.success('¡Invitación aceptada! El evento ya aparece en tus eventos.');
      
      // Redirect to events page after a short delay
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
      
    } catch (error) {
      console.error('Error accepting invitation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al aceptar la invitación';
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = () => {
    console.log("Rejecting partner invitation");
    onReject();
  };

  // Safe function to get initials from partner name
  const getPartnerInitials = () => {
    if (invitationData.partnerAvatar) {
      return invitationData.partnerAvatar;
    }
    if (invitationData.partnerName) {
      return invitationData.partnerName.split(' ').map(n => n[0]).join('');
    }
    return '??';
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-0">
      {/* Mobile-optimized Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Invitación de Pareja</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">Revisa los detalles y confirma tu participación</p>
        </div>
        <Button variant="outline" onClick={onBack} className="self-start sm:self-auto">
          Volver
        </Button>
      </div>

      {/* Partner Information - Mobile Optimized */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-primary" />
            Invitación Recibida
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-primary/5 rounded-lg">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm sm:text-lg font-bold shrink-0">
              {getPartnerInitials()}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-foreground truncate">{invitationData.partnerName || 'Usuario'}</h3>
              <p className="text-muted-foreground text-sm sm:text-base">Te ha invitado a formar pareja</p>
              <Badge className="mt-2 bg-green-100 text-green-800 text-xs">
                {invitationData.inviterRegistration?.category && invitationData.inviterRegistration?.subcategory 
                  ? `${invitationData.inviterRegistration.category} - ${invitationData.inviterRegistration.subcategory}`
                  : invitationData.inviterRegistration?.category || eventDetails.category || 'Categoría por definir'
                }
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Information - Mobile Optimized */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="h-5 w-5 text-orange-600" />
            Detalles del Evento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Event Title */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-foreground">{eventDetails.title}</h3>
          </div>

          {/* Event Details Grid - Mobile Friendly */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-foreground text-sm sm:text-base">{eventDetails.date}</p>
                {eventDetails.time && (
                  <p className="text-muted-foreground text-xs sm:text-sm">{eventDetails.time}</p>
                )}
              </div>
            </div>
            
            {eventDetails.location && (
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-foreground text-sm sm:text-base">{eventDetails.location}</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">Madrid</p>
                </div>
              </div>
            )}
          </div>

          {/* Event Tags - Mobile Optimized */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">{eventDetails.sport}</Badge>
            {eventDetails.category && (
              <Badge variant="secondary" className="text-xs">{eventDetails.category}</Badge>
            )}
            <Badge variant="secondary" className="text-xs font-semibold">€{eventDetails.registrationFee} por persona</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
        <Button 
          onClick={handleReject}
          variant="outline" 
          className="flex-1 flex items-center justify-center gap-2 border-destructive/20 text-destructive hover:bg-destructive/10 h-12 text-sm sm:text-base"
        >
          <X className="h-4 w-4" />
          Rechazar Invitación
        </Button>
        <Button 
          onClick={handleAccept}
          disabled={isProcessing}
          className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white h-12 text-sm sm:text-base"
        >
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Aceptando...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4" />
              Aceptar Invitación
            </div>
          )}
        </Button>
      </div>

      {/* Important Notice - Mobile Optimized */}
      <Card className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 shrink-0"></div>
            <div>
              <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                <strong>Importante:</strong> Al aceptar esta invitación, confirmas tu participación en el evento junto con {invitationData.partnerName || 'tu compañero'}. 
                Tu compañero ya ha pagado la inscripción completa por ambos, por lo que no necesitas realizar ningún pago adicional.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerInvitationConfirmation;
